import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Settings } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { formatRelativeTimeIso } from '@/lib/time-format'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationItem,
} from '@/hooks/use-notifications'
import { useNotificationsSse } from '@/hooks/use-notifications-sse'
import { useFocusStore } from '@/hooks/use-focus-store'
import { audienceLabel } from '@/components/notifications/notification-utils'

const RECENT_LIMIT = 8

interface NotificationBellProps {
  /** Header chrome icon button styling from App.tsx. */
  buttonClassName?: string
}

export function NotificationBell({ buttonClassName }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { data, isLoading } = useNotifications({ limit: RECENT_LIMIT })
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const { focus } = useFocusStore()
  useNotificationsSse()

  const items = data?.items ?? []
  const unreadCount = data?.unread_count ?? 0
  // Audience chips only help users who wear more than one hat; for
  // single-focus users every notification is theirs, so the chip is noise.
  const showAudienceChips = (focus?.available_foci?.length ?? 0) > 1

  const handleItemClick = (item: NotificationItem) => {
    setOpen(false)
    if (!item.read_at) {
      markRead.mutate(item.id)
    }
    if (item.action_url) {
      navigate(item.action_url)
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn('relative', buttonClassName)}
          title="Notifications"
          aria-label={
            unreadCount > 0
              ? `Notifications (${unreadCount} unread)`
              : 'Notifications'
          }
        >
          <Bell className="h-4 w-4 shrink-0" />
          {unreadCount > 0 && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
          {unreadCount > 0 && (
            <button
              type="button"
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Loading…
            </p>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              You're all caught up.
            </p>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item)}
                className="flex w-full items-start gap-2 border-b px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/60"
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    item.read_at ? 'bg-transparent' : 'bg-primary'
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-sm',
                      item.read_at ? 'text-muted-foreground' : 'font-medium'
                    )}
                  >
                    {item.title}
                  </span>
                  {item.body && (
                    <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                      {item.body}
                    </span>
                  )}
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground/80">
                    {formatRelativeTimeIso(item.created_at)}
                    {showAudienceChips && item.audience && (
                      <span className="rounded-full border border-border px-1.5 py-px text-[10px] font-medium leading-none text-muted-foreground">
                        {audienceLabel(item.audience)}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
        <div className="flex items-center justify-between border-t px-3 py-2">
          <button
            type="button"
            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => {
              setOpen(false)
              navigate('/notifications')
            }}
          >
            View all
          </button>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => {
              setOpen(false)
              navigate('/settings/notifications')
            }}
          >
            <Settings className="h-3.5 w-3.5" />
            Settings
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
